import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useLocalStorage, useTimeoutFn } from "react-use";
import { motion } from "framer-motion";
import { SidebarNav } from "@/components/sidebar/Sidebar";
import { PromptLibraryContextProvider } from "@/modules/promptLibrary/promptLibrary.context";
import { isRestrictedEsgUser } from "@/hooks/use-esg-restriction";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useMainwebWarm } from "@/hooks/use-mainweb-warm";
import { Loader } from "@/components/ui/loader";
import { GlobalUploadProvider } from "@/views/drive/upload/GlobalUploadProvider";

export const RootLayout: React.FC = () => {
  const location = useLocation();
  const authenticatedUser = useAuthenticatedUser();
  // Gated on a confirmed session: the wake ping must never be reachable from an
  // anonymous visitor who merely mounted the shell on a protected URL.
  const isMainwebWarm = useMainwebWarm(authenticatedUser.isAuthenticated);
  const [sidebarPinned, setSidebarPinned] = useLocalStorage("sidebarPinned", false);

  const handleSidebarPinChange = React.useCallback(
    (isPinned: boolean) => {
      setSidebarPinned(isPinned);
    },
    [setSidebarPinned],
  );

  const sidebarWidth = React.useMemo(() => (sidebarPinned === true ? "18rem" : "4rem"), [sidebarPinned]);
  const loginPath = React.useMemo(() => {
    const redirectPath = `${location.pathname}${location.search}`;
    return `/login?state=${encodeURIComponent(btoa(redirectPath))}`;
  }, [location.pathname, location.search]);

  const isResolvingAuth =
    authenticatedUser.isLoading || (authenticatedUser.isFetching && !authenticatedUser.isAuthenticated);

  // Under web scale-to-zero a cold pf-auth buffers the auth check for the full
  // ~12s wake — but cold implies no valid session can exist (KEDA cooldown >=
  // the token inactivity window), so a slow check already means "not logged
  // in". Redirect optimistically instead of holding a spinner; the login page
  // keeps warming pf-auth in the background and bounces authenticated users
  // straight back in on the rare race.
  const [authWaitExpired, setAuthWaitExpired] = React.useState(false);
  useTimeoutFn(() => setAuthWaitExpired(true), 2_000);

  if (isResolvingAuth && authWaitExpired) {
    return <Navigate to={loginPath} replace />;
  }

  if (isResolvingAuth) {
    return (
      <div className="flex h-screen items-center justify-center bg-ui-app-background">
        <Loader className="h-8 w-8 text-coral" />
      </div>
    );
  }

  if (!authenticatedUser.isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  if (isRestrictedEsgUser(authenticatedUser)) {
    return <Navigate to="/esg" replace />;
  }

  // Credentials check out, but pf-mainweb may still be waking from zero: every
  // view behind this layout reads from it, and Drive would paint its skeleton
  // for the whole ~10-15s wake. Hold the same spinner the auth check uses until
  // the wake ping comes back, so the app appears once it has real data to show.
  // Bounded by MAINWEB_WARMUP_TIMEOUT_MS (helpers/warmup.ts) — a wake that runs
  // long falls back to the skeleton rather than spinning forever.
  if (!isMainwebWarm) {
    return (
      <div className="flex h-screen items-center justify-center bg-ui-app-background">
        <Loader className="h-8 w-8 text-coral" />
      </div>
    );
  }

  return (
    <PromptLibraryContextProvider>
      <GlobalUploadProvider>
        <div className="flex h-screen flex-grow bg-ui-app-background" data-testid="root-layout">
          <div className="relative flex flex-grow flex-col">
            <SidebarNav isPinned={Boolean(sidebarPinned)} onPinChange={handleSidebarPinChange} />
            <motion.div
              className="relative flex flex-grow flex-col"
              initial={false}
              style={{
                width: "calc(100vw - " + sidebarWidth + ")",
              }}
              animate={{
                marginLeft: sidebarWidth,
              }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <main className="flex flex-grow flex-col overflow-auto">
                <div className="flex flex-grow flex-col px-4 py-8 sm:px-6 lg:px-8">
                  <Outlet />
                </div>
              </main>
            </motion.div>
          </div>
        </div>
      </GlobalUploadProvider>
    </PromptLibraryContextProvider>
  );
};
